import {
  DEV_SIZE_MAX,
  DEV_SIZE_MIN,
  DEV_SIZE_ROOT,
  getDevicesD3Style,
  pureRecalcDevSize,
  sendNewSize,
  updateDevsAfterResize
} from "src/components/Planner/planner/planner";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { SERIAL_SPACING, getDColsNum, getDRowsNum } from "src/components/Planner/planner/serial-layout";
import {
  getD3DevTopCenter,
  getDeviceById,
  isD3DevSnapped,
  SER_MARG,
  uDev
} from "src/components/Planner/planner/uDev";
import { uDevDOM } from "src/components/Planner/planner/uDev-dom";
import {
  _does,
  doesDeviceFitRoom,
  snapDeviceToWall,
  uMath
} from "src/components/Planner/planner/uMath";
import {redrawDevices} from "src/components/Planner/planner/deviceDrawing";
import {startLoading, stopLoading} from "src/components/Planner/controller";

export function previewDeviceSize(previewLenOf1Px, previewSizeMod, oldSize, newSize) {
  const d3Devs = getDevicesD3Style();
  return redrawDevicesWithNewSize(oldSize, newSize, d3Devs);
}

/**
 *
 * @param {number} oldSize
 * @param {number} newSize
 * @param {D3Device[]} d3Devs
 */
export function redrawDevicesWithNewSize(oldSize, newSize, d3Devs) {
  // save data
  const saveState = saveDevicesPosStates();

  // manipulate positions
  d3Devs.forEach(d3Dev => {
    const firstDev = d3Dev.devices[0];
    ['x', 'y', 'angle', 'roomId'].forEach(propKey => {
      d3Dev[propKey] = firstDev[propKey];
    })
    const cols = getDColsNum(d3Dev);
    const rows = getDRowsNum(d3Dev);

    // get old size
    PlannerGlobals.DEVICE_SIZE = oldSize
    const snappedToWallId = isD3DevSnapped(d3Dev, true); // 2nd parameter (true) means: return wallId if snapped, not only true/false
    const topMid = snappedToWallId ? {wallId: snappedToWallId, ...getD3DevTopCenter(d3Dev)} : null;
    // temp new devsize for snap
    PlannerGlobals.DEVICE_SIZE = newSize;
    const snappedPt = snappedToWallId ? snapDeviceToWall(topMid, newSize * 10, cols, undefined, undefined, undefined, undefined, undefined, rows) : null;
    PlannerGlobals.DEVICE_SIZE = oldSize;

    if (snappedPt) {
      uDevDOM.devicesOfD3D(d3Dev).forEach(dev => {
        dev.x = snappedPt.point.x;
        dev.y = snappedPt.point.y;
        dev.angle = snappedPt.rotation;
        dev.roomId = snappedPt.room.id;
      });
    }
    else {
      const oldWidth = getFrameLengthWithProps(cols, oldSize, SERIAL_SPACING);
      const newWidth = getFrameLengthWithProps(cols, newSize, SERIAL_SPACING);
      const oldHeight = getFrameLengthWithProps(rows, oldSize, SERIAL_SPACING);
      const newHeight = getFrameLengthWithProps(rows, newSize, SERIAL_SPACING);

      const x = firstDev.x - (newWidth - oldWidth) / 2;
      const y = firstDev.y - (newHeight - oldHeight) / 2;

      uDevDOM.devicesOfD3D(d3Dev).forEach(dev => {
        dev.x = x;
        dev.y = y;
      });

      // temp new devsize for fit check
      PlannerGlobals.DEVICE_SIZE = newSize;
      if (!doesDeviceFitRoom(firstDev.roomId, firstDev, firstDev.angle, cols, rows)) {
        const snappedPt = snapDeviceToWall(firstDev, PlannerGlobals.DEVICE_SIZE * 10, cols, true, undefined, undefined, undefined, undefined, rows);
        if (snappedPt) {
          uDevDOM.devicesOfD3D(d3Dev).forEach(dev => {
            dev.x = snappedPt.point.x;
            dev.y = snappedPt.point.y;
            dev.angle = snappedPt.rotation;
            dev.roomId = snappedPt.room.id;
          });
        }
        else {
          // ERROR!!!
          console.debug('error');
        }
      }
      else {
        uDevDOM.devicesOfD3D(d3Dev).forEach(dev => {
          dev.x = x;
          dev.y = y;
        });
      }
      PlannerGlobals.DEVICE_SIZE = oldSize;
    }
  });

  // draw preview
  PlannerGlobals.DEVICE_SIZE = newSize;
  redrawDevices();

  // return d3Devs

  // check for differences
  const differentDevs = [];
  for (let deviceId in saveState) {
    const devProps = saveState[deviceId];
    const realDev = getDeviceById(deviceId);
    for (let propKey in devProps) {
      let devVal = realDev[propKey];
      let propVal = devProps[propKey];
      if (typeof devVal === "number") {
        devVal = +devVal.toFixed(3);
      }
      if (typeof propVal === "number") {
        propVal = +propVal.toFixed(3);
      }

      if (devVal != propVal) {
        const clone = Object.assign({}, realDev);

        differentDevs.push(clone);
        break;
      }
    }
  }

  // restore
  PlannerGlobals.DEVICE_SIZE = oldSize;
  restoreDevicesPosStates(saveState);
  PlannerGlobals.DEVICE_SIZE = newSize

  return differentDevs;
}

export function getDeviceSize(lengthOf1Px, sizeMod) {
  if (!lengthOf1Px) { lengthOf1Px = 1; }
  const baseDeviceSize = uMath.clamp(DEV_SIZE_ROOT / lengthOf1Px, DEV_SIZE_MIN, DEV_SIZE_MAX);
  console.log( baseDeviceSize )
  const deviceSize = uMath.clamp(baseDeviceSize * sizeMod / 100, 2, 100);
  return deviceSize;
}

export function getSerMarg(baseSerMarg, deviceSize) {
  return baseSerMarg * deviceSize / 20;
}

// addSerMarg = false - added because it is not clear why we need to add extra margins 
// and this has a bad effect on the placement of devices when switching between icon sizes
export function getFrameLengthWithProps(devNum, deviceSize, serialSpacing, addSerMarg = false) {
  if (!devNum) { return 0; }
  return (addSerMarg ? getSerMarg(SER_MARG, deviceSize) * 2 : 0) + devNum * deviceSize + (devNum - 1) * serialSpacing;
}

/**
 *
 * @returns {Object<string, {x:number,y:number,angle:number,roomId:number}>}
 */
export function saveDevicesPosStates() {
  const saveState = {}
  const propsToSave = ['x', 'y', 'angle', 'roomId'];

  PlannerGlobals.devices.forEach(dev => {
    saveState[dev.deviceId] = {};
    propsToSave.forEach(propKey => {
      saveState[dev.deviceId][propKey] = dev[propKey];
    });
  });

  return saveState;
}

export function restoreDevicesPosStates(saveState) {
  for (let deviceId in saveState) {
    const savedDevProps = saveState[deviceId];
    const dev = getDeviceById(deviceId);
    if (dev) {
      for (let propKey in savedDevProps) {
        dev[propKey] = savedDevProps[propKey];
      }
    }
  }
}

/**
 *
 * @param {Device[]} devs
 * @param {number} newSizeMod
 * @returns
 */
export function updateAllDevs(devs, newSizeMod) {
  const propsToSave = ['x', 'y', 'angle', 'roomId'];
  startLoading()
  document.body.classList.add("resize-loading");

  const st = performance.now();
  devs.forEach(dev => {
    const realDev = getDeviceById(dev.deviceId);
    if (realDev) {
      propsToSave.forEach(propKey => {
        realDev[propKey] = dev[propKey];
      });
    }
  });

  return updateDevsAfterResize(devs)
  .then(() => new Promise(res => {
    PlannerGlobals.DEVICE_SIZE_MOD = newSizeMod;
    pureRecalcDevSize();
    sendNewSize(() => res());
  }))
  .then(() => {
    // redrawDevices();
    const et = performance.now();
    console.log(`took ${Math.round(et - st)}ms`);
  })
  .finally(() => {
    stopLoading();
    document.body.classList.remove("resize-loading");
    // checkTooBigDevices(false);
  });

}

/**
 *
 * @param {number} newSizeMod
 * @returns
 */
export function DEBUG_updateNSend(prevSizeMod, newSizeMod, newSize, oldSize) {
  newSizeMod = +newSizeMod;
  if (!_does([25, 100, 150, 200]).contain(newSizeMod)) {
    console.debug('New device size modifier can only be 50, 100, 150');
    return Promise.reject();
  }
  return updateAllDevs(previewDeviceSize(PlannerGlobals.LENGTHOF1PX, prevSizeMod, oldSize, newSize), newSizeMod);
}
